/**
 * CommissionReportCard — "Send to Shannon" on the Commission board (Oct 28, 2026).
 *
 * Greg: "Just 1 with that weeks commissions on it for all. Should there be a button for Chad to click when done and
 * send to Shannon?" Chad approves deals one at a time; when the week's approvals are done, Chad presses the button and ONE
 * email goes to Shannon (a copy to Greg): every person, their total, each job with its math, the grand total
 * (supabase/functions/commission-payout-report). It holds every approved commission, RS pool and TL incentive
 * payout that is on no report yet, so anything approved after the click waits for the next one.
 *
 * The card shows what is waiting and how the last report went (live: the report log is in realtime). The server
 * decides who may do what (commission_payout_report_status): the approvers send; finance and admin can send a
 * failed report again; everyone else who sees the board reads it.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import {
  confirmText, lastLine, namesOf, normalizeStatus, pendingLine, sendLabel, type ReportStatus,
} from "../../lib/commissionPayoutReport";

const TONE_CLASS = {
  ok: "text-emerald-700",
  busy: "text-sky-700",
  bad: "text-rose-700",
  muted: "text-slate-500",
} as const;

export default function CommissionReportCard() {
  const [status, setStatus] = useState<ReportStatus | null>(null);
  const [hidden, setHidden] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc("commission_payout_report_status");
    if (error) {
      // the database is older than this screen (the function is not there yet): show nothing
      if ((error as { code?: string }).code === "PGRST202") { setHidden(true); return; }
      setErr(formatSupabaseError(error));
      return;
    }
    setHidden(false);
    setStatus(normalizeStatus(data));
  }, []);

  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("commission_payout_reports", () => { void load(); });
  useRealtimeTable("books_payout_bills", () => { void load(); });

  async function send() {
    setBusy(true);
    setErr(null);
    const { error } = await supabase.rpc("commission_payout_report_send");
    setBusy(false);
    setConfirming(false);
    if (error) setErr(formatSupabaseError(error));
    void load();
  }

  async function resend(id: string) {
    setBusy(true);
    setErr(null);
    const { error } = await supabase.rpc("commission_payout_report_resend", { p_id: id });
    setBusy(false);
    if (error) setErr(formatSupabaseError(error));
    void load();
  }

  if (hidden || (!status && !err)) return null;

  const last = status ? lastLine(status) : null;
  const canSend = !!status && status.mode === "on" && status.can_send && status.pending.bills > 0;
  const canResend = !!status?.last && status.can_resend && (status.last.status === "failed" || status.last.status === "skipped");

  return (
    <div className="bg-white rounded-xl shadow p-3 mb-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-bardin-ink">
            📧 Commission report{status?.to.length ? ` to ${namesOf(status.to)}` : ""}
          </div>
          {status && status.mode === "off" ? (
            <p className="text-xs text-slate-500 mt-0.5">The commission report is switched off.</p>
          ) : status ? (
            <>
              <p className={"text-xs mt-0.5 " + (status.pending.bills > 0 ? "text-amber-700 font-medium" : "text-slate-500")}>
                {pendingLine(status)}
              </p>
              {status.pending.bills > 0 && !status.can_send && (
                <p className="text-[11px] text-slate-500 mt-0.5">Chad sends it once the week's approvals are done.</p>
              )}
            </>
          ) : null}
          {last && (
            <p className={"text-[11px] mt-0.5 " + TONE_CLASS[last.tone]}>
              {last.text}
              {canResend && status?.last && (
                <button
                  type="button"
                  onClick={() => resend(status.last!.id)}
                  disabled={busy}
                  className="ml-2 underline font-medium disabled:opacity-50"
                >
                  Send it again
                </button>
              )}
            </p>
          )}
          {err && <p className="text-[11px] text-rose-700 mt-0.5">{err}</p>}
        </div>
        {status && canSend && !confirming && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50 flex-shrink-0"
          >
            {sendLabel(status)}
          </button>
        )}
      </div>
      {status && confirming && (
        <div className="mt-2 px-3 py-2 rounded bg-amber-50 border border-amber-200">
          <p className="text-xs text-amber-800">{confirmText(status)}</p>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={send}
              disabled={busy}
              className="px-3 py-1 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Sending…" : `✓ ${sendLabel(status)}`}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="px-3 py-1 text-xs font-medium rounded bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
